import React, { useEffect, useRef, useState } from "react";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

/**
 * Cloudflare Turnstile widget.
 * - Renders nothing when REACT_APP_TURNSTILE_SITE_KEY is empty (graceful bypass).
 * - Calls onToken(token | null) when challenge state changes.
 */
export default function TurnstileWidget({ onToken, theme = "dark" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [scriptReady, setScriptReady] = useState(
    typeof window !== "undefined" && !!window.turnstile
  );

  // Poll briefly for the Turnstile script to be ready (it's loaded async in index.html)
  useEffect(() => {
    if (!SITE_KEY || scriptReady) return;
    let cancelled = false;
    const t0 = Date.now();
    const id = setInterval(() => {
      if (cancelled) return;
      if (window.turnstile) {
        setScriptReady(true);
        clearInterval(id);
      } else if (Date.now() - t0 > 8000) {
        clearInterval(id);
      }
    }, 150);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [scriptReady]);

  useEffect(() => {
    if (!SITE_KEY || !scriptReady || !containerRef.current) return;
    if (widgetIdRef.current) return; // already rendered

    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        theme,
        "refresh-expired": "auto",
        retry: "auto",
        callback: (token) => onToken && onToken(token),
        "expired-callback": () => onToken && onToken(null),
        "error-callback": () => onToken && onToken(null),
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Turnstile render failed:", e);
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptReady]);

  if (!SITE_KEY) return null;
  return <div ref={containerRef} data-testid="turnstile-widget" className="mt-1" />;
}

export const TURNSTILE_ENABLED = !!SITE_KEY;
